import type { RequestHandler } from "msw";
import type { ScenarioWorkspaceScope } from "./scenarioWorkspaceScope.ts";

export interface ScenarioDependency {
    /** Key of the scenario this dependency is evaluated against. */
    key: string;
    /** The state the referenced scenario must be in for this scenario to be enabled. */
    requiredState: boolean;
}

export type ScenarioGroup =
    | "Tenant Setup"
    | "Initial Crawl"
    | "Workspace"
    | "Protect"
    | "Migrate"
    | "MigrationProjects"
    | "MigrationProjects - Plan"
    | "Partner"
    | "Billing"
    | "General";

export interface ScenarioDefinition {
    key: string;
    label: string;
    description?: string;
    /** Section this scenario is grouped under in the MSW Scenarios picker UI. */
    group: ScenarioGroup;
    /** Other scenarios whose state gates this one. When unmet, this scenario is disabled and forced off. */
    dependsOn?: readonly ScenarioDependency[];
    /**
     * Names a workspace scope registered in scenarioWorkspaceScope.ts. The scenario only takes effect
     * while that workspace is active, so the picker disables it on any other one. Unlike dependsOn,
     * this is not forced off when unmet — the scenario stays enabled for the workspace it applies to.
     */
    workspaceScope?: ScenarioWorkspaceScope;
    /**
     * Enabled until this specific key is persisted, so turning it off sticks while an unrelated
     * saved scenario does not suppress it. A default never overrides a persisted choice it conflicts
     * with through dependsOn. "Reset All" clears the store and restores the default.
     */
    isEnabledByDefault?: boolean;
}

const billingSubscriptionScenarioKeys = [
    "billingActiveTrial",
    "billingExpiredTrial",
    "billingMigrateOnly",
    "billingMigrateOnlyExpiring",
    "billingBothSameEndDate",
    "billingBothSameEndDateExpiring",
    "billingMigrateExpiresFirst",
    "billingProtectExpiresFirst",
    "billingMigratePro",
    "billingMigrateUltimate",
    "billingProtectOnly",
    "billingEssentialsMailbox"
] as const;

function billingSubscriptionScenario<const K extends (typeof billingSubscriptionScenarioKeys)[number]>(
    key: K,
    label: string,
    isEnabledByDefault = false
) {
    return {
        key,
        label,
        description: "Pricing V2 scenario map, shown on the billing subscriptions page",
        group: "Billing" as const,
        dependsOn: billingSubscriptionScenarioKeys
            .filter(other => other !== key)
            .map(other => ({ key: other, requiredState: false })),
        isEnabledByDefault
    };
}

export const scenarioDefinitions = [
    {
        key: "crawling",
        label: "Crawl in Progress",
        description: "Simulate an ongoing crawl across all entity sets",
        group: "Initial Crawl"
    },
    {
        key: "permissionsEnrichment",
        label: "Permissions Enrichment In Progress",
        description: "Simulate a completed initial crawl with permissions enrichment still running",
        group: "Initial Crawl"
    },
    {
        key: "initialCrawl",
        label: "Initial Crawl In Progress",
        description: "Simulate the tenant's initial crawl still scanning, for the header widget",
        group: "Initial Crawl",
        dependsOn: [
            { key: "initialCrawlProtectCompleteMigrateScanning", requiredState: false },
            { key: "initialCrawlProtectScanningMigrateComplete", requiredState: false },
            { key: "initialCrawlError", requiredState: false },
            { key: "initialCrawlPartiallyScanned", requiredState: false },
            { key: "initialCrawlFinishing", requiredState: false }
        ]
    },
    {
        key: "initialCrawlProtectCompleteMigrateScanning",
        label: "Initial Crawl - Protect Complete, Migrate Scanning",
        description: "Simulate the Protect tenant done scanning while the Migrate assessment tenant is still crawling",
        group: "Initial Crawl",
        dependsOn: [
            { key: "initialCrawl", requiredState: false },
            { key: "initialCrawlProtectScanningMigrateComplete", requiredState: false },
            { key: "initialCrawlError", requiredState: false },
            { key: "initialCrawlPartiallyScanned", requiredState: false },
            { key: "initialCrawlFinishing", requiredState: false }
        ]
    },
    {
        key: "initialCrawlProtectScanningMigrateComplete",
        label: "Initial Crawl - Protect Scanning, Migrate Complete",
        description: "Simulate the Migrate assessment tenant done scanning while the Protect tenant is still crawling",
        group: "Initial Crawl",
        dependsOn: [
            { key: "initialCrawl", requiredState: false },
            { key: "initialCrawlProtectCompleteMigrateScanning", requiredState: false },
            { key: "initialCrawlError", requiredState: false },
            { key: "initialCrawlPartiallyScanned", requiredState: false },
            { key: "initialCrawlFinishing", requiredState: false }
        ]
    },
    {
        key: "initialCrawlPartiallyScanned",
        label: "Initial Crawl - Partially Scanned",
        description:
            "Simulate a scan midway through: the first categories are done and the rest still running, which is the only state that shows the scan card's progress bar and its mix of done and running rows",
        group: "Initial Crawl",
        dependsOn: [
            { key: "initialCrawl", requiredState: false },
            { key: "initialCrawlProtectCompleteMigrateScanning", requiredState: false },
            { key: "initialCrawlProtectScanningMigrateComplete", requiredState: false },
            { key: "initialCrawlError", requiredState: false },
            { key: "initialCrawlFinishing", requiredState: false }
        ]
    },
    {
        key: "initialCrawlFinishing",
        label: "Initial Crawl - Finishes While You Watch",
        description:
            "Simulate a scan that reports one more progress update and then completes, so the scan card clearing and the panel's empty state can be watched live",
        group: "Initial Crawl",
        dependsOn: [
            { key: "initialCrawl", requiredState: false },
            { key: "initialCrawlProtectCompleteMigrateScanning", requiredState: false },
            { key: "initialCrawlProtectScanningMigrateComplete", requiredState: false },
            { key: "initialCrawlPartiallyScanned", requiredState: false },
            { key: "initialCrawlError", requiredState: false }
        ]
    },
    {
        key: "initialCrawlError",
        label: "Initial Crawl Overview Fails to Load",
        description: "Simulate the scan overview request failing, for the activity panel's failed-load state",
        group: "Initial Crawl",
        dependsOn: [
            { key: "initialCrawl", requiredState: false },
            { key: "initialCrawlProtectCompleteMigrateScanning", requiredState: false },
            { key: "initialCrawlProtectScanningMigrateComplete", requiredState: false },
            { key: "initialCrawlPartiallyScanned", requiredState: false },
            { key: "initialCrawlFinishing", requiredState: false }
        ]
    },
    {
        key: "readOnly",
        label: "Read-Only Mode",
        description:
            "Simulate a workspace without the Remediation add-on, so Protect is read-only. Not the Microsoft remediation consent, which is a separate grant.",
        group: "Tenant Setup"
    },
    {
        key: "noRegion",
        label: "No Region",
        description: "Simulate a workspace with no data region configured, triggering the region setup redirect",
        group: "Tenant Setup"
    },
    {
        key: "profileRegionEurope",
        label: "Profile Region: Europe",
        description:
            "Simulate a workspace saved in Europe whatever the browser's region, e.g. a fresh browser that defaults to North America",
        group: "Tenant Setup",
        dependsOn: [{ key: "noRegion", requiredState: false }]
    },
    {
        key: "trialReasonRequired",
        label: "Trial Reason Required",
        description: "Simulate a new trial user who has not yet answered the trial-reason prompt",
        group: "General"
    },
    {
        key: "noPrimaryTenant",
        label: "No Primary Tenant",
        description: "Simulate a workspace with no primary tenant configured, triggering the tenant setup redirect",
        group: "Tenant Setup"
    },
    {
        key: "noMigrateAssessmentTenant",
        label: "No Migrate-Assessment Tenant",
        description: "Simulate a workspace with no migrate-assessment tenant configured",
        group: "Tenant Setup"
    },
    {
        key: "notConsented",
        label: "Not Consented",
        description: "Simulate a workspace that has not consented to the required Microsoft 365 permissions",
        group: "Tenant Setup"
    },
    {
        key: "missingPermissions",
        label: "Missing Permissions",
        description: "Simulate a consented primary tenant that lacks some permissions, showing the reconsent callout",
        group: "Tenant Setup",
        dependsOn: [{ key: "notConsented", requiredState: false }]
    },
    {
        key: "manyClientWorkspaces",
        label: "25 Client Workspaces",
        description:
            "Add 21 extra client workspaces (25 total), to exercise the client-workspaces table's Show all/Show less toggle",
        group: "General"
    },
    {
        key: "partnerProvisioned",
        label: "Partner-Provisioned Workspace",
        description: "Simulate a workspace whose subscription was provisioned by a partner",
        group: "Workspace"
    },
    {
        key: "workspaceMember",
        label: "No Admin Role",
        description: "Simulate a non-admin workspace member (default session is admin)",
        group: "Workspace"
    },
    {
        key: "resellerManaged",
        label: "Reseller-Managed Workspace",
        description:
            "Simulate a reseller-managed workspace, which shows the reseller CTA instead of a purchase link. Combine with an expiring or expired scenario to see it on the banner",
        group: "Workspace"
    },
    {
        key: "sharedSubscription",
        label: "Shared Protect/Migrate Subscription",
        description:
            "Simulate one subscription granting both products (a ShareGate Trial), which the banner counts once instead of twice",
        group: "Workspace"
    },
    {
        key: "partnerPremierTier",
        label: "Premier Tier",
        workspaceScope: "partner",
        description: "Simulate the partner workspace on the Premier tier instead of Standard",
        group: "Partner",
        dependsOn: [{ key: "partnerEliteTier", requiredState: false }]
    },
    {
        key: "partnerEliteTier",
        label: "Elite Tier",
        workspaceScope: "partner",
        description: "Simulate the partner workspace on the Elite tier instead of Standard",
        group: "Partner",
        dependsOn: [{ key: "partnerPremierTier", requiredState: false }],
        isEnabledByDefault: true
    },
    {
        key: "partnerCanCreateWorkspace",
        label: "Can Create Workspaces",
        workspaceScope: "partner",
        description:
            "Simulate the partner being entitled to create client workspaces, which shows the create button on the dashboard",
        group: "Partner",
        isEnabledByDefault: true
    },
    {
        key: "partnerCanOpenLegacyAssessment",
        label: "Legacy Assessment Access",
        workspaceScope: "partner",
        description:
            "Simulate the account having access to the legacy Migration assessment, which shows the Migration assessment card on the dashboard even without Migration Projects access",
        group: "Partner"
    },
    {
        key: "noProtectAccess",
        label: "No Access",
        description: "Simulate a user without access to the Protect product",
        group: "Protect",
        dependsOn: [{ key: "noProtectSubscription", requiredState: false }]
    },
    {
        key: "noProtectSubscription",
        label: "No Subscription",
        description: "Simulate a workspace with no active Protect subscription",
        group: "Protect"
    },
    {
        key: "protectTrial",
        label: "Trial Mode",
        description: "Simulate a Protect subscription on a free trial",
        group: "Protect",
        dependsOn: [{ key: "noProtectSubscription", requiredState: false }]
    },
    {
        key: "protectExpiring",
        label: "Expiring",
        description: "Simulate a Protect subscription nearing its renewal date (expires in 14 days)",
        group: "Protect",
        dependsOn: [
            { key: "noProtectSubscription", requiredState: false },
            { key: "protectGracePeriod", requiredState: false },
            { key: "protectExpiredPastGracePeriod", requiredState: false }
        ]
    },
    {
        key: "protectGracePeriod",
        label: "Expired - Grace Period",
        description:
            "Simulate an expired Protect subscription within the data-retention window (read-only). Requires Unified Experience to also be enabled",
        group: "Protect",
        dependsOn: [
            { key: "noProtectSubscription", requiredState: false },
            { key: "protectExpiring", requiredState: false },
            { key: "protectExpiredPastGracePeriod", requiredState: false }
        ]
    },
    {
        key: "protectExpiredPastGracePeriod",
        label: "Expired - After Grace Period",
        description:
            "Simulate a Protect subscription expired past the 90-day data-retention window (grace period ended)",
        group: "Protect",
        dependsOn: [
            { key: "noProtectSubscription", requiredState: false },
            { key: "protectExpiring", requiredState: false },
            { key: "protectGracePeriod", requiredState: false }
        ]
    },
    {
        key: "protectBlockedSubscription",
        label: "Blocked Subscription",
        description: "Simulate a Protect subscription blocked due to a payment issue",
        group: "Protect",
        dependsOn: [{ key: "noProtectSubscription", requiredState: false }]
    },
    {
        key: "removeDirectPermissionBulkActionFailure",
        label: "Remove Direct Permission Fails",
        description: 'Simulate the PMR\'s "Remove permission" bulk action failing once started',
        group: "Protect"
    },
    {
        key: "goalSummaryError",
        label: "Goal Summary Error",
        description: "Simulate the recommendations goal summary request returning a business error",
        group: "Protect"
    },
    {
        key: "goalSummaryUnavailable",
        label: "Goal Summary Unavailable",
        description: "Simulate the recommendations goal summary request being unavailable",
        group: "Protect"
    },
    {
        key: "recommendationActionBadRequest",
        label: "Recommendation Action Bad Request",
        description: "Simulate a recommendation action request being rejected as invalid",
        group: "Protect"
    },
    {
        key: "recommendationActionNotFound",
        label: "Recommendation Action Not Found",
        description: "Simulate a recommendation action request targeting a recommendation that no longer exists",
        group: "Protect"
    },
    {
        key: "recommendationActionConflict",
        label: "Recommendation Action Conflict",
        description: "Simulate a recommendation action request conflicting with the recommendation's current state",
        group: "Protect"
    },
    {
        key: "bulkOperationFailed",
        label: "Bulk Operation Fails",
        description: "Show one failed bulk operation in the Activity panel, with a single failure message",
        group: "Protect"
    },
    {
        key: "bulkOperationFailedWithMixedErrors",
        label: "Bulk Operation Fails With Mixed Errors",
        description: "Show one failed bulk operation whose failed items report different messages",
        group: "Protect"
    },
    {
        key: "bulkOperationPartiallyFailed",
        label: "Bulk Operation Partially Fails",
        description:
            "Run one bulk operation that ends in partial failure, which is what renders the in-page partial-failure callout",
        group: "Protect"
    },
    {
        key: "bulkOperationsTargets",
        label: "Bulk Operations Targets",
        description:
            "List three running operations started 4h, 12m and 2m ago: a trim on 4 workspaces, group owners added to 4 groups, and a trim on a single workspace, so the Activity panel shows each card's target line",
        group: "Protect"
    },
    {
        key: "bulkOperationRunning",
        label: "Bulk Operation Running",
        description:
            "Keep one bulk operation in progress for about 30 seconds before it succeeds, so the running tag, the Activity panel's workspace count, navigation and the end-of-run toast are all observable",
        group: "Protect"
    },
    {
        key: "bulkOperationsMixed",
        label: "Bulk Operations Mixed",
        description:
            "List running and failed bulk operations started by a person, a policy, MCP and a review: review ones stay out of the Activity panel, and no summary loads, so progress is indeterminate",
        group: "Protect"
    },
    {
        key: "bulkOperationsListError",
        label: "Bulk Operations List Error",
        description: "Fail the Activity panel's bulk operations list while leaving the scans untouched",
        group: "Protect"
    },
    {
        key: "canStartProtectTrial",
        label: "Can Start Trial",
        description:
            "Simulate a workspace eligible to start a Protect trial. Only visible on the Protect tile while there is no active subscription, so combine it with No Subscription or an expired scenario",
        group: "Protect"
    },
    {
        key: "protectNfrSubscription",
        label: "NFR Subscription",
        description: "Simulate a Not-For-Resale Protect subscription, which badges the Protect tile",
        group: "Protect",
        dependsOn: [{ key: "noProtectSubscription", requiredState: false }]
    },
    {
        key: "noMigrateAccess",
        label: "No Access",
        description: "Simulate a user without access to the Migrate product",
        group: "Migrate",
        dependsOn: [{ key: "noMigrateSubscription", requiredState: false }]
    },
    {
        key: "noMigrateSubscription",
        label: "No Subscription",
        description: "Simulate a workspace with no active Migrate subscription",
        group: "Migrate"
    },
    {
        key: "migrateTrial",
        label: "Trial Mode",
        description: "Simulate a Migrate subscription on a free trial",
        group: "Migrate",
        dependsOn: [{ key: "noMigrateSubscription", requiredState: false }]
    },
    {
        key: "migrateExpiring",
        label: "Expiring",
        description: "Simulate a Migrate subscription nearing its renewal date (expires in 14 days)",
        group: "Migrate",
        dependsOn: [
            { key: "noMigrateSubscription", requiredState: false },
            { key: "migrateGracePeriod", requiredState: false },
            { key: "migrateExpiredPastGracePeriod", requiredState: false }
        ]
    },
    {
        key: "migrateGracePeriod",
        label: "Expired - Grace Period",
        description: "Simulate an expired Migrate subscription within the data-retention window",
        group: "Migrate",
        dependsOn: [
            { key: "noMigrateSubscription", requiredState: false },
            { key: "migrateExpiring", requiredState: false },
            { key: "migrateExpiredPastGracePeriod", requiredState: false }
        ]
    },
    {
        key: "migrateExpiredPastGracePeriod",
        label: "Expired - After Grace Period",
        description:
            "Simulate a Migrate subscription expired past the 90-day data-retention window (grace period ended)",
        group: "Migrate",
        dependsOn: [
            { key: "noMigrateSubscription", requiredState: false },
            { key: "migrateExpiring", requiredState: false },
            { key: "migrateGracePeriod", requiredState: false }
        ]
    },
    {
        key: "migrateBlockedSubscription",
        label: "Blocked Subscription",
        description: "Simulate a Migrate subscription blocked due to a payment issue",
        group: "Migrate",
        dependsOn: [{ key: "noMigrateSubscription", requiredState: false }]
    },
    {
        key: "canStartMigrateTrial",
        label: "Can Start Trial",
        description:
            "Simulate a workspace eligible to start a Migrate trial. Only partner-provisioned workspaces are ever eligible, so this requires Partner-Provisioned Workspace to be enabled first",
        group: "Migrate",
        dependsOn: [{ key: "partnerProvisioned", requiredState: true }]
    },
    {
        key: "migrateNfrSubscription",
        label: "NFR Subscription",
        description: "Simulate a Not-For-Resale Migrate subscription, which badges the Migrate tile",
        group: "Migrate",
        dependsOn: [{ key: "noMigrateSubscription", requiredState: false }]
    },
    {
        key: "migrationProjectsEnabled",
        label: "Enabled",
        description: "Simulate a workspace with the Migration Projects product enabled",
        group: "MigrationProjects"
    },
    {
        key: "noMigrationProjectsAccess",
        label: "No Access",
        description: "Simulate a user without access to Migration Projects (the Move app's project list)",
        group: "MigrationProjects",
        dependsOn: [{ key: "migrationProjectsEnabled", requiredState: true }]
    },
    {
        key: "noMigrationProjects",
        label: "Empty Project List",
        description: "Simulate a workspace with Migration Projects enabled but no projects created yet",
        group: "MigrationProjects",
        dependsOn: [
            { key: "migrationProjectsEnabled", requiredState: true },
            { key: "migrationProjectPendingConsent", requiredState: false }
        ]
    },
    {
        key: "migrationProjectPendingConsent",
        label: "Project Pending Consent",
        description:
            "Simulate one migration project not yet connected to a Microsoft tenant, to test the consent/association flow",
        group: "MigrationProjects",
        dependsOn: [
            { key: "migrationProjectsEnabled", requiredState: true },
            { key: "noMigrationProjects", requiredState: false }
        ]
    },
    {
        key: "migrationProjectsTrial",
        label: "Trial Mode",
        description: "Simulate a Migration Projects subscription on a free trial",
        group: "MigrationProjects",
        dependsOn: [{ key: "migrationProjectsEnabled", requiredState: true }]
    },
    {
        key: "migrationProjectsLimitReached",
        label: "Project Limit Reached",
        description:
            "Simulate a Migration Projects subscription capped at its current project count (the default project list has 5 projects)",
        group: "MigrationProjects",
        dependsOn: [{ key: "migrationProjectsEnabled", requiredState: true }]
    },
    {
        key: "migrationProjectsExpiring",
        label: "Expiring",
        description: "Simulate a Migration Projects subscription nearing its renewal date (expires in 14 days)",
        group: "MigrationProjects",
        dependsOn: [
            { key: "migrationProjectsEnabled", requiredState: true },
            { key: "migrationProjectsGracePeriod", requiredState: false },
            { key: "migrationProjectsExpiredPastGracePeriod", requiredState: false }
        ]
    },
    {
        key: "migrationProjectsGracePeriod",
        label: "Expired - Grace Period",
        description: "Simulate an expired Migration Projects subscription within the data-retention window",
        group: "MigrationProjects",
        dependsOn: [
            { key: "migrationProjectsEnabled", requiredState: true },
            { key: "migrationProjectsExpiring", requiredState: false },
            { key: "migrationProjectsExpiredPastGracePeriod", requiredState: false }
        ]
    },
    {
        key: "migrationProjectsExpiredPastGracePeriod",
        label: "Expired - After Grace Period",
        description:
            "Simulate a Migration Projects subscription expired past the 90-day data-retention window (grace period ended)",
        group: "MigrationProjects",
        dependsOn: [
            { key: "migrationProjectsExpiring", requiredState: false },
            { key: "migrationProjectsGracePeriod", requiredState: false }
        ]
    },
    {
        key: "migrationPlanIsPlanned",
        label: "Is Planned",
        description:
            "Simulate a planned project: Identities and OneDrive figures ready, SharePoint computing, Teams failed",
        group: "MigrationProjects - Plan",
        dependsOn: [{ key: "migrationProjectsEnabled", requiredState: true }]
    },
    billingSubscriptionScenario("billingActiveTrial", "1. Active trial"),
    billingSubscriptionScenario("billingExpiredTrial", "2. Expired trial"),
    billingSubscriptionScenario("billingMigrateOnly", "3. Migrate only"),
    billingSubscriptionScenario("billingMigrateOnlyExpiring", "4. Migrate only, expiring"),
    billingSubscriptionScenario("billingBothSameEndDate", "5. Both products, same end date"),
    billingSubscriptionScenario("billingBothSameEndDateExpiring", "6. Both products, same end date, expiring", true),
    billingSubscriptionScenario("billingMigrateExpiresFirst", "7. Different end dates, Migrate expires first"),
    billingSubscriptionScenario("billingProtectExpiresFirst", "8. Different end dates, Protect expires first"),
    billingSubscriptionScenario("billingMigratePro", "9. Migrate Pro"),
    billingSubscriptionScenario("billingMigrateUltimate", "10. Migrate Ultimate"),
    billingSubscriptionScenario("billingProtectOnly", "11. Protect only"),
    billingSubscriptionScenario("billingEssentialsMailbox", "12. Essentials Mailbox")
] as const satisfies readonly ScenarioDefinition[];

export type ScenarioKey = (typeof scenarioDefinitions)[number]["key"];

export type ScenarioMap = Partial<Record<ScenarioKey, RequestHandler[]>>;

export function mergeScenarios(...maps: ScenarioMap[]): ScenarioMap {
    const merged: ScenarioMap = {};

    for (const map of maps) {
        for (const key in map) {
            const handlers = map[key as ScenarioKey];

            if (!handlers) {
                continue;
            }

            merged[key as ScenarioKey] = [...(merged[key as ScenarioKey] ?? []), ...handlers];
        }
    }

    return merged;
}

const definitions: readonly ScenarioDefinition[] = scenarioDefinitions;

// Layers the default-on scenarios under a persisted set. A persisted key always wins, and a default
// whose dependsOn conflicts with what is already persisted is skipped rather than fighting it — so a
// developer who chose Premier keeps it instead of being flipped to a newly defaulted Elite.
export function applyScenarioDefaults(persisted: Record<string, boolean>): Record<string, boolean> {
    const result = { ...persisted };

    for (const definition of definitions) {
        if (
            definition.isEnabledByDefault &&
            !(definition.key in persisted) &&
            isScenarioAllowed(definition.key, result)
        ) {
            result[definition.key] = true;
        }
    }

    return resolveScenarioState(result);
}

// Returns the first unmet dependency for a scenario, if any, so callers can explain why it's disabled.
export function getBlockingDependency(
    key: string,
    enabledKeys: Record<string, boolean>
): ScenarioDependency | undefined {
    const definition = definitions.find(d => d.key === key);

    return definition?.dependsOn?.find(dep => (enabledKeys[dep.key] ?? false) !== dep.requiredState);
}

export function isScenarioAllowed(key: string, enabledKeys: Record<string, boolean>): boolean {
    return getBlockingDependency(key, enabledKeys) === undefined;
}

// Turns off any enabled scenario whose "dependsOn" conditions are no longer met, cascading until stable.
export function resolveScenarioState(enabledKeys: Record<string, boolean>): Record<string, boolean> {
    const result = { ...enabledKeys };
    let changed = true;

    while (changed) {
        changed = false;

        for (const definition of definitions) {
            if (result[definition.key] && !isScenarioAllowed(definition.key, result)) {
                result[definition.key] = false;
                changed = true;
            }
        }
    }

    return result;
}

export function getScenarioHandlers(allScenarios: ScenarioMap, enabledKeys: Record<string, boolean>): RequestHandler[] {
    const result: RequestHandler[] = [];

    for (const [key, handlers] of Object.entries(allScenarios)) {
        if (enabledKeys[key] && handlers) {
            result.push(...handlers);
        }
    }

    return result;
}
